import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Shuffle, Eraser, ChevronLeft, ChevronRight } from "lucide-react";
import { monthRange, monthOf, daysBetween, groupByWeek, findExclusion, fmtShort, addDays } from "@/lib/vigilanza";
import { ShiftRow } from "@/components/vigilanza/ShiftRow";

const err = (e) => toast.error(e.response?.data?.detail || "Errore");

export function CalendarTab({ slots, people, exclusions }) {
  const qc = useQueryClient();
  const [slotId, setSlotId] = useState(null);
  const [range, setRange] = useState(monthRange());
  const [overwrite, setOverwrite] = useState(false);
  useEffect(() => { if (!slotId && slots.length) setSlotId(slots[0].id); }, [slots, slotId]);
  const slot = slots.find((s) => s.id === slotId);

  const key = ["vig-shifts", slotId, range.from, range.to];
  const { data } = useQuery({
    queryKey: key, enabled: !!slotId && !!range.from && !!range.to && range.from <= range.to,
    queryFn: async () => (await api.get("/vigilanza/shifts", { params: { from: range.from, to: range.to, slot_id: slotId } })).data,
  });
  const byDate = useMemo(() => Object.fromEntries((data?.shifts || []).map((s) => [s.date, s])), [data]);
  const eligible = people.filter((p) => p.active && (!p.slot_ids?.length || p.slot_ids.includes(slotId)));
  const counts = useMemo(() => {
    const c = {};
    (data?.shifts || []).forEach((s) => { if (s.person_id) c[s.person_id] = (c[s.person_id] || 0) + 1; });
    return c;
  }, [data]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["vig-shifts"] });
  const genMut = useMutation({
    mutationFn: async () => (await api.post("/vigilanza/generate", { slot_id: slotId, date_from: range.from, date_to: range.to, overwrite })).data,
    onSuccess: (r) => { toast.success(`Rotazione creata: ${r.assigned} turni assegnati, ${r.skipped_excluded} giorni esclusi`); refresh(); },
    onError: err,
  });
  const clearMut = useMutation({
    mutationFn: async () => (await api.post("/vigilanza/clear", { slot_id: slotId, date_from: range.from, date_to: range.to })).data,
    onSuccess: (r) => { toast.success(`${r.deleted} turni rimossi`); refresh(); },
    onError: err,
  });
  const saveMut = useMutation({
    mutationFn: async (body) => (await api.put("/vigilanza/shifts", { slot_id: slotId, ...body })).data,
    onSuccess: refresh, onError: err,
  });

  const shiftMonth = (n) => setRange(monthOf(range.from, n));
  const days = range.from && range.to && range.from <= range.to ? daysBetween(range.from, range.to) : [];
  const weeks = groupByWeek(days, (d) => d);

  return (
    <div className="space-y-5 mt-4">
      <div className="flex gap-2 flex-wrap" data-testid="vig-slot-picker">
        {slots.map((s) => (
          <button key={s.id} onClick={() => setSlotId(s.id)} data-testid={`vig-slot-${s.id}`}
            className={`px-4 py-2 rounded-full border text-sm transition-colors ${s.id === slotId ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"}`}>
            <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ background: s.color }}/>
            {s.name} <span className="opacity-70 font-mono text-xs">{s.start_time}–{s.end_time}</span>
            {!s.active && <span className="ml-1 text-[10px] uppercase">· disattiva</span>}
          </button>
        ))}
      </div>

      <Card className="p-4 border border-stone-200 shadow-none rounded-xl flex flex-wrap items-end gap-3">
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} data-testid="vig-prev-month"><ChevronLeft size={16}/></Button>
        <div><Label className="text-xs">Dal</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="vig-range-from" className="w-40"/></div>
        <div><Label className="text-xs">Al</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="vig-range-to" className="w-40"/></div>
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} data-testid="vig-next-month"><ChevronRight size={16}/></Button>
        <label className="flex items-center gap-2 text-sm text-stone-600 pb-2">
          <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="vig-overwrite"/> Riassegna anche i giorni già compilati
        </label>
        <div className="flex gap-2 ml-auto">
          <Button onClick={() => genMut.mutate()} disabled={!slotId || genMut.isPending || !days.length} className="bg-[#1C352D] hover:bg-[#2a4a40]" data-testid="vig-generate">
            <Shuffle size={14} className="mr-1"/> {genMut.isPending ? "Genero…" : "Genera rotazione equa"}
          </Button>
          <Button variant="outline" onClick={() => window.confirm(`Svuotare i turni ${slot?.name} dal ${fmtShort(range.from)} al ${fmtShort(range.to)}?`) && clearMut.mutate()} disabled={!slotId || !days.length} data-testid="vig-clear">
            <Eraser size={14} className="mr-1"/> Svuota periodo
          </Button>
        </div>
      </Card>

      {eligible.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="vig-counts">
          {eligible.map((p) => (
            <span key={p.id} className="text-xs px-2.5 py-1 rounded-full bg-white border border-stone-200">
              {p.name} · <b className="font-mono">{counts[p.id] || 0}</b>
            </span>
          ))}
        </div>
      )}
      {eligible.length === 0 && <div className="text-sm text-amber-700">Nessuna persona abilitata per questa fascia: aggiungile nella scheda Persone.</div>}

      <div className="space-y-5">
        {weeks.map((w) => (
          <div key={w.monday}>
            <div className="text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-1.5">Settimana {fmtShort(w.monday)} – {fmtShort(addDays(w.monday, 6))}</div>
            <Card className="border border-stone-200 shadow-none rounded-xl overflow-hidden divide-y divide-stone-100">
              {w.items.map((d) => (
                <ShiftRow key={d} date={d} slot={slot} shift={byDate[d]} people={people} eligible={eligible}
                  exclusion={slot && findExclusion(exclusions, slot.id, d)} onSave={(body) => saveMut.mutate({ date: d, ...body })}/>
              ))}
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}
